// =============================================================================
// SettingsBilling.js ,  /settings/billing
//
// Hybrid customer portal:
//   • NATIVE in this page: current plan, trial status, billing cycle, next
//     renewal date, payment method (masked), recent invoices.
//   • DELEGATED to hosted portal (Stripe / Razorpay) for the irreversible
//     actions: change card, cancel, update tax info, download original PDF.
//
// Phase 32 scaffold, the "Manage subscription" CTA currently hits the
// placeholder /api/billing/portal endpoint which returns 501. The UI shows a
// calm "coming soon" toast instead of crashing. When Stripe/Razorpay keys
// land (Step G), the endpoint will return a real redirect URL and we follow
// it. No frontend changes will be needed at that point.
// =============================================================================
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft, Sparkles, CreditCard, Receipt, ExternalLink, ShieldCheck,
  CalendarClock, AlertCircle, Star, ArrowRight, X, Check,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { getBillingMe, startCheckout, skipTrial, openRazorpayModal, verifyRazorpaySignature } from '@/lib/billing';
import { supabase } from '@/lib/supabaseClient';
import { effectivePlan, planMeta } from '@/lib/entitlements';
import { formatMoney } from '@/lib/format';
import { PLANS, getPlanPrice, BILLING_CYCLES } from '@/lib/pricing';
import useFocusRefetch from '@/hooks/useFocusRefetch';
import usePageTitle from '@/hooks/usePageTitle';
import { useConfirm } from '@/components/ConfirmDialog';

const STATUS_LABELS = {
  free:      { label: 'Free',          tone: 'neutral' },
  trialing:  { label: 'Trial',         tone: 'gold'    },
  active:    { label: 'Active',        tone: 'green'   },
  past_due:  { label: 'Payment due',   tone: 'amber'   },
  canceled:  { label: 'Canceled',      tone: 'amber'   },
  expired:   { label: 'Expired',       tone: 'neutral' },
};

function StatusBadge({ status }) {
  const meta = STATUS_LABELS[status] || STATUS_LABELS.free;
  const toneClass = {
    green:   'bg-primary/10 text-primary border-primary/20',
    gold:    'bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))] border-[hsl(var(--gold))]/30',
    amber:   'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    neutral: 'bg-muted text-muted-foreground border-border',
  }[meta.tone] || '';
  return (
    <Badge variant="outline" className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${toneClass}`} data-testid="billing-status-badge">
      {meta.label}
    </Badge>
  );
}

function fmtDate(iso) {
  if (!iso) return ' ';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) { return iso; }
}

export default function SettingsBilling() {
  usePageTitle('Billing');
  const { user, profile, session, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Trial users haven't paid yet so they have no `billing_cycle` set. We
  // expose a small Monthly/Annual switch right inside the trial card so they
  // can pick before clicking "Add payment method".
  const [selectedCycle, setSelectedCycle] = useState('monthly');
  // Phase 37, pending-upgrade confirmation card. When the user arrives via
  // /pricing → ?upgrade=<plan>&cycle=<cycle>, we DO NOT auto-fire Razorpay.
  // Instead we stash the intent here and render an explicit "Confirm upgrade"
  // card with a prominent cycle toggle + price preview. Two reasons:
  //   1. The Razorpay hosted checkout page only shows ONE cycle (whichever
  //      we created the subscription with). Users were missing the annual
  //      option because the toggle was buried in a different card.
  //   2. Auto-redirecting on page load meant a user who cancelled out of
  //      Razorpay would land back here, immediately get punched into another
  //      Razorpay session, and never see their plan state, which read as a
  //      mysterious downgrade.
  // The user must explicitly click "Confirm and continue to payment" to leave
  // the app. Cancelling stays on this page with full plan visibility.
  const [pendingUpgrade, setPendingUpgrade] = useState(null); // { plan, cycle }
  const confirm = useConfirm();

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!session?.access_token || !user?.id) return;
    // Phase 37.1, never blank the UI with skeletons when the page is
    // already mounted. The initial mount uses `silent:false` so first paint
    // shows a skeleton; every subsequent refresh (tab focus, post-checkout
    // return, manual refetch) uses `silent:true` so the data updates in
    // place without a jarring re-skeleton flash.
    if (!silent) setLoading(true);
    const [me, invs] = await Promise.all([
      getBillingMe(session.access_token),
      // Best-effort: the `invoices` table may not exist yet (Phase 32 schema
      // pending). We swallow the error and show an empty state.
      supabase.from('invoices').select('*').eq('user_id', user.id).order('created_at', { ascending: false }).limit(5)
        .then(r => r.data || []).catch(() => []),
    ]);
    setBilling(me);
    setInvoices(invs);
    if (!silent) setLoading(false);
  }, [session?.access_token, user?.id]);

  useEffect(() => { load(); }, [load]);

  // Phase 37, pick up ?upgrade=<plan>&cycle=<cycle> from /pricing and
  // STAGE it as a pending upgrade. We deliberately do NOT call /api/billing/checkout
  // here. The user must click "Confirm" first. Params are stripped so a
  // hard refresh doesn't repeatedly re-stage.
  useEffect(() => {
    if (loading) return;
    const upgradeTo = (searchParams.get('upgrade') || '').toLowerCase();
    const cycleQ = (searchParams.get('cycle') || '').toLowerCase();
    if (!upgradeTo) return;
    if (!['clarity', 'horizon', 'summit'].includes(upgradeTo)) return;
    const cycle = ['monthly', 'annual'].includes(cycleQ) ? cycleQ : 'monthly';
    setSelectedCycle(cycle);
    setPendingUpgrade({ plan: upgradeTo, cycle });
    setSearchParams({}, { replace: true });
    // Scroll the confirmation card into view on next tick so it's the first
    // thing a returning user sees.
    setTimeout(() => {
      try {
        document.getElementById('billing-confirm-upgrade')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) { /* no-op */ }
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, searchParams]);

  // Phase 37, when the user returns to this tab (e.g. after closing a
  // Razorpay checkout window without paying), silently refresh both the
  // AuthContext profile AND our local billing snapshot.
  //
  // Phase 37.1 follow-up, use the shared `useFocusRefetch` hook so:
  //   • It only fires after the tab has been hidden ≥ 60s (no thrashing on
  //     casual alt-tab cadence).
  //   • The handler is silent (`load({ silent: true })`), never blanks
  //     the UI with skeletons. The previous implementation re-loaded loudly
  //     on every focus event and made the page feel broken.
  useFocusRefetch(() => {
    refreshProfile?.();
    load({ silent: true });
  });

  const openPortal = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/portal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.url) { window.location.href = data.url; return; }
      }
      if (res.status === 503) {
        toast.info('No active subscription found', {
          description: 'Once you subscribe, your management page will open here. Use Cancel/Upgrade above instead.',
        });
      } else if (res.status === 501) {
        toast.info('Customer portal is being set up', {
          description: 'For billing changes right now, email support, we’ll respond within one business day.',
        });
      } else {
        toast.error('Could not open the billing portal. Please try again.');
      }
    } catch (e) {
      toast.error('Network error, please try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const startUpgrade = async (plan, cycle) => {
    if (busy) return;
    setBusy(true);
    const res = await startCheckout(session?.access_token, plan, cycle);
    // If the backend returned an error response (no subscription was
    // created), surface it and let the user retry on this page.
    if (!res || res?.error || !res?.subscription_id) {
      setBusy(false);
      if (res?.code === 503) {
        toast.info('Coming soon', {
          description: res?.error || 'This currency isn’t available yet, we’ll email you the moment it is.',
        });
      } else if (res?.code === 501) {
        toast.info('Checkout is launching soon', {
          description: 'Payments aren’t live yet, we’ll email you the moment they are.',
        });
      } else {
        toast.error(res?.error || 'Could not start checkout right now.');
      }
      return;
    }

    // Phase 37.2, preferred path: open Razorpay's embedded Checkout modal
    // so the 2120 layout stays visible behind the payment dialog. If the
    // SDK can't load (network failure, ad-blocker, etc.) we transparently
    // fall back to the hosted `short_url` redirect.
    const opened = await openRazorpayModal({
      key_id: res.key_id,
      subscription_id: res.subscription_id,
      prefill: res.prefill || {},
      notes: res.notes || {},
      currency: res.currency,
      onSuccess: async (resp) => {
        // resp: { razorpay_payment_id, razorpay_subscription_id, razorpay_signature }
        const v = await verifyRazorpaySignature(session?.access_token, {
          razorpay_payment_id: resp?.razorpay_payment_id,
          razorpay_subscription_id: resp?.razorpay_subscription_id,
          razorpay_signature: resp?.razorpay_signature,
        });
        if (v?.ok) {
          toast.success('Payment received', {
            description: 'We’re finalizing your subscription, your plan will update in a moment.',
          });
          setPendingUpgrade(null);
          // Silent refresh, webhook normally updates DB within seconds.
          await refreshProfile?.();
          await load({ silent: true });
        } else {
          toast.error('We couldn’t verify the payment automatically', {
            description: 'If you were charged, please contact support, we’ll reconcile it for you.',
          });
        }
        setBusy(false);
      },
      onDismiss: () => {
        // User closed the modal without paying, stay on this page with
        // their current plan intact.
        setBusy(false);
        toast.info('Checkout cancelled', {
          description: 'Your current plan is unchanged. Continue when you’re ready.',
        });
      },
      onFailure: (err) => {
        setBusy(false);
        toast.error(err?.description || 'Payment failed. Please try again or use a different card.');
      },
    });

    if (!opened) {
      // SDK failed to load, fall back to the hosted redirect path.
      toast.info('Opening checkout in a new page…');
      setPendingUpgrade(null);
      window.location.href = res.short_url || res.url;
      return;
    }
    // Modal is open. `setBusy(false)` happens inside the handlers above.
  };

  const cancelPendingUpgrade = () => {
    setPendingUpgrade(null);
    toast.info('Upgrade cancelled', { description: 'Your current plan is unchanged.' });
  };

  const handleSkipTrial = async () => {
    if (busy) return;
    const ok = await confirm({
      title: 'Switch to Free forever?',
      description: 'Your trial ends now and you go to the Free plan. AI insights, statement imports, and reflections will be turned off. This cannot be undone, your one-time trial will be consumed.',
      confirmLabel: 'Switch to Free',
      cancelLabel: 'Keep my trial',
      tone: 'destructive',
    });
    if (!ok) return;
    setBusy(true);
    const res = await skipTrial(session?.access_token);
    setBusy(false);
    if (res?.ok) {
      toast.success('Switched to Free forever', {
        description: 'No card, no time pressure. Upgrade anytime from Pricing.',
      });
      load();
    } else {
      toast.error(res?.error || 'Could not switch to Free right now.');
    }
  };

  // --- Render guards -------------------------------------------------------
  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
      </div>
    );
  }

  const eff = effectivePlan(profile);
  const meta = planMeta(eff);
  const status = billing?.plan_status || (eff === 'free' ? 'free' : 'active');
  const isTrialing = !!billing?.trial?.is_on_trial;
  const isPaid = eff !== 'free' && !isTrialing;
  const sub = billing?.subscription;
  // Phase 46.2, Private beta detection. Beta users get full plan access at
  // no charge; we hide every upgrade/payment surface and show a calm card
  // explaining the arrangement. Defence-in-depth: the backend also blocks
  // /api/billing/checkout for beta users, so even a misclick here is safe.
  const isBeta = !!profile?.is_beta;
  const betaPlan = profile?.beta_plan;
  const betaEndsAt = profile?.beta_ends_at;

  // --- Private beta, short-circuit the entire billing surface ------------
  // Renders ONLY the beta explanation card. There are no upgrade buttons,
  // cycle toggles, payment cards, or cancel CTAs on screen, literally no
  // path to the gateway. (Backend /api/billing/checkout also returns 403
  // for beta users, so even a forged request can't charge a card.)
  if (isBeta) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="billing-page-beta">
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => navigate('/settings')} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-1" data-testid="billing-back">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to settings
            </button>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Billing & subscription</h1>
            <p className="text-sm text-muted-foreground mt-1">Your beta access in one glance.</p>
          </div>
        </div>
        <div
          className="rounded-2xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--sage))]/15 p-6"
          data-testid="billing-beta-state"
        >
          <div className="flex items-start gap-4">
            <div className="rounded-xl bg-[hsl(var(--primary))]/15 p-3 shrink-0">
              <Sparkles className="h-5 w-5 text-[hsl(var(--primary))]" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-lg font-semibold tracking-tight">
                You&rsquo;re on the 2120 Private Beta
              </h2>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                Your account has full {betaPlan ? (betaPlan.charAt(0).toUpperCase() + betaPlan.slice(1)) : 'Summit'} access at no charge for the duration of the program. No card, no subscription, no billing of any kind. {betaEndsAt ? (
                  <>We&rsquo;ll reach out before <span className="font-medium text-foreground">{new Date(betaEndsAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span> with options for staying on after beta.</>
                ) : (
                  <>We&rsquo;ll reach out before the program ends with options for staying on after beta.</>
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-3">
                Your feedback shapes what we ship. Use the feedback button in the corner to share anything , bugs, ideas, whisper-level annoyances.
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-5">
          <h3 className="font-display text-sm font-semibold tracking-tight mb-1.5">What&rsquo;s included</h3>
          <ul className="text-sm text-muted-foreground space-y-1.5">
            <li>&bull; Full access to every feature on the {betaPlan ? (betaPlan.charAt(0).toUpperCase() + betaPlan.slice(1)) : 'Summit'} plan</li>
            <li>&bull; Generous onboarding-pool credits so you can backfill several months of history at once</li>
            <li>&bull; Priority support , replies usually within a working day</li>
            <li>&bull; Direct line to the team via the feedback widget</li>
          </ul>
        </div>
      </div>
    );
  }

  // --- Page ---------------------------------------------------------------
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="billing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/settings')} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-1" data-testid="billing-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to settings
          </button>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Billing & subscription</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your plan, payment method, and invoices.</p>
        </div>
      </div>

      {/* Beta users hit the early-return above, so this surface is rendered
          only for paying / trialing / free users. */}

      {/* Phase 37, Confirm Upgrade card.
          Shown ONLY when the user arrives from /pricing with a staged
          upgrade. Solves two bugs at once:
            • Razorpay's hosted page can't toggle monthly/annual, so the
              cycle must be chosen here, FRONT AND CENTER.
            • Cancelling out of Razorpay used to silently bounce the user
              back into another checkout, they never saw their plan card.
              Now they land on this card and can either retry or cancel. */}
      {pendingUpgrade && (() => {
        const region = (billing?.currency === 'INR' || profile?.currency === 'INR') ? 'india' : 'international';
        const planDef = PLANS.find(p => p.id === pendingUpgrade.plan);
        if (!planDef) return null;
        const monthlyPrice = getPlanPrice(planDef, region, 'monthly');
        const annualPrice = getPlanPrice(planDef, region, 'annual');
        const chosen = selectedCycle === 'annual' ? annualPrice : monthlyPrice;
        const monthlyEquiv = selectedCycle === 'annual' ? annualPrice?.monthlyEquiv : null;
        const annualSavings = monthlyPrice?.amount
          ? Math.max(0, Math.round((monthlyPrice.amount * 12 - annualPrice.amount) * 100) / 100)
          : 0;
        const decimals = chosen.currency === 'USD' ? 2 : 0;
        return (
          <Card
            id="billing-confirm-upgrade"
            className="rounded-2xl border-[hsl(var(--gold))]/40 ring-1 ring-[hsl(var(--gold))]/15 bg-[hsl(var(--gold))]/[0.04]"
            data-testid="billing-confirm-upgrade-card"
          >
            <CardContent className="p-5 sm:p-6 space-y-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--gold))] flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" /> Confirm your upgrade
                  </div>
                  <div className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-1.5" data-testid="confirm-upgrade-plan-name">
                    {planDef.name}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 max-w-md leading-relaxed">{planDef.tagline}</p>
                </div>
                <button
                  type="button"
                  onClick={cancelPendingUpgrade}
                  data-testid="confirm-upgrade-cancel"
                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 rounded-lg px-2 py-1 hover:bg-muted/60"
                  title="Cancel upgrade"
                >
                  <X className="h-3.5 w-3.5" /> Cancel
                </button>
              </div>

              {/* Cycle toggle, the headline UI of this card. Renders both
                  prices side-by-side so users can see exactly what they
                  save by going annual BEFORE leaving the app. */}
              <div className="grid sm:grid-cols-2 gap-3" data-testid="confirm-upgrade-cycles">
                {['monthly', 'annual'].map((c) => {
                  const isSelected = selectedCycle === c;
                  const p = c === 'annual' ? annualPrice : monthlyPrice;
                  const per = c === 'annual' ? 'year' : 'month';
                  const me = c === 'annual' ? p?.monthlyEquiv : null;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setSelectedCycle(c)}
                      data-testid={`confirm-cycle-${c}`}
                      className={`text-left rounded-xl p-4 border transition-all ${isSelected
                        ? 'border-[hsl(var(--gold))] bg-card ring-2 ring-[hsl(var(--gold))]/30 shadow-sm'
                        : 'border-border/60 bg-card/50 hover:border-border hover:bg-card'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium capitalize flex items-center gap-2">
                          {c === 'annual' ? 'Annual' : 'Monthly'}
                          {c === 'annual' && (
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--gold))] bg-[hsl(var(--gold))]/12 border border-[hsl(var(--gold))]/30 rounded-full px-2 py-0.5">
                              2 mo free
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <div className="h-5 w-5 rounded-full bg-[hsl(var(--gold))] grid place-items-center">
                            <Check className="h-3 w-3 text-[hsl(var(--gold-foreground))]" strokeWidth={3} />
                          </div>
                        )}
                      </div>
                      <div className="mt-2 flex items-baseline gap-1.5">
                        <div className="font-display text-2xl font-semibold tabular-nums tracking-tight">
                          {formatMoney(p.amount, p.currency, { decimals: p.currency === 'USD' ? 2 : 0 })}
                        </div>
                        <div className="text-xs text-muted-foreground">/ {per}</div>
                      </div>
                      {me ? (
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          ≈ {formatMoney(me, p.currency, { decimals: p.currency === 'USD' ? 2 : 0 })}/month
                        </div>
                      ) : c === 'monthly' && annualSavings > 0 ? (
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          Save {formatMoney(annualSavings, p.currency, { decimals: p.currency === 'USD' ? 0 : 0 })} on annual
                        </div>
                      ) : (
                        <div className="text-[11px] text-muted-foreground mt-0.5">&nbsp;</div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Total summary */}
              <div className="rounded-xl bg-card border border-border/60 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="confirm-upgrade-summary">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">You’ll be charged</div>
                  <div className="font-display text-2xl font-semibold tabular-nums tracking-tight mt-0.5" data-testid="confirm-upgrade-amount">
                    {formatMoney(chosen.amount, chosen.currency, { decimals })}
                    <span className="text-sm text-muted-foreground font-normal ml-1">
                      / {selectedCycle === 'annual' ? 'year' : 'month'}
                    </span>
                  </div>
                  {monthlyEquiv && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      ≈ {formatMoney(monthlyEquiv, chosen.currency, { decimals })}/month · billed annually
                    </div>
                  )}
                </div>
                <Button
                  onClick={() => startUpgrade(pendingUpgrade.plan, selectedCycle)}
                  disabled={busy}
                  data-testid="confirm-upgrade-continue"
                  className="rounded-xl bg-[hsl(var(--gold))] text-[hsl(var(--gold-foreground))] hover:bg-[hsl(var(--gold))] hover:brightness-95 focus-visible:ring-2 focus-visible:ring-[hsl(var(--gold))]/40 shadow-sm"
                >
                  {busy ? 'Opening checkout…' : <>Continue to payment <ArrowRight className="h-4 w-4 ml-1.5" /></>}
                </Button>
              </div>

              <p className="text-[11px] text-muted-foreground leading-relaxed">
                You’ll see Razorpay’s secure checkout modal next, your 2120 layout stays visible behind it.
                Cancel anytime, your current plan stays in place until you finish checkout.
                {billing?.trial?.is_on_trial && (
                  <> Your <span className="font-medium text-foreground">{planMeta(effectivePlan(profile)).label} trial</span> remains active until you successfully subscribe.</>
                )}
              </p>
            </CardContent>
          </Card>
        );
      })()}

      {/* Current plan card */}
      <Card className="rounded-2xl border-border/70" data-testid="billing-current-plan">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Current plan</div>
              <div className="font-display text-2xl font-semibold tracking-tight mt-1 flex items-center gap-3">
                {meta.label}
                <StatusBadge status={status} />
              </div>
              {isTrialing && (
                <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--gold))]" />
                  {billing?.trial?.days_left} days left on your free trial
                </div>
              )}
              {isPaid && billing?.current_period_end && (
                <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5" />
                  Renews {fmtDate(billing.current_period_end)} · {billing.billing_cycle || sub?.billing_cycle || 'monthly'}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {eff === 'free' && (
                <Button onClick={() => navigate('/pricing')} className="rounded-xl" data-testid="billing-upgrade-cta">
                  See plans
                </Button>
              )}
              {(isPaid || isTrialing) && (
                <Button onClick={openPortal} disabled={busy} variant="outline" className="rounded-xl" data-testid="billing-portal-cta">
                  Manage subscription <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              )}
            </div>
          </div>

          {isTrialing && (() => {
            // Derive total trial length from subscription dates when available
            // (Phase 32 schema). Falls back to 14 days otherwise; clamps so
            // legacy 30-day trials don't render negative progress.
            const tStart = sub?.trial_start ? new Date(sub.trial_start) : null;
            const tEnd = sub?.trial_end ? new Date(sub.trial_end) : null;
            const totalDays = (tStart && tEnd)
              ? Math.max(1, Math.round((tEnd - tStart) / (1000 * 60 * 60 * 24)))
              : 14;
            const daysLeft = Math.max(0, Math.min(totalDays, billing?.trial?.days_left || 0));
            const daysUsed = totalDays - daysLeft;
            const pct = Math.min(100, Math.max(0, (daysUsed / totalDays) * 100));
            return (
              <>
                <Separator className="my-5" />
                <div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                    <span>Trial progress</span>
                    <span data-testid="billing-trial-progress-label">{daysUsed} / {totalDays} days used</span>
                  </div>
                  <Progress value={pct} className="h-1.5" />
                  <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                    Add a payment method anytime to continue your {meta.label} access after the trial. We never charge before the trial ends, and we’ll send a gentle reminder a few days before.
                  </p>

                  {/* Phase 37.1, bigger, more obvious billing cycle picker
                      with inline prices and explicit savings copy. The old
                      tiny segmented control didn't communicate VALUE, users
                      couldn't tell what they'd be charged or what they'd
                      save by going annual. */}
                  {(() => {
                    const region = (billing?.currency === 'INR' || profile?.currency === 'INR') ? 'india' : 'international';
                    const planDef = PLANS.find(p => p.id === eff);
                    if (!planDef) return null;
                    const monthlyP = getPlanPrice(planDef, region, 'monthly');
                    const annualP = getPlanPrice(planDef, region, 'annual');
                    const annualSavings = monthlyP?.amount
                      ? Math.max(0, Math.round((monthlyP.amount * 12 - annualP.amount) * 100) / 100)
                      : 0;
                    const decimals = monthlyP.currency === 'USD' ? 2 : 0;
                    return (
                      <div className="mt-5" data-testid="billing-cycle-switch">
                        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">Choose a billing cycle</div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {['monthly', 'annual'].map((c) => {
                            const isSelected = selectedCycle === c;
                            const p = c === 'annual' ? annualP : monthlyP;
                            const per = c === 'annual' ? 'year' : 'month';
                            const me = c === 'annual' ? p?.monthlyEquiv : null;
                            return (
                              <button
                                key={c}
                                type="button"
                                onClick={() => setSelectedCycle(c)}
                                data-testid={`billing-cycle-${c}`}
                                className={`text-left rounded-xl p-4 border transition-all ${isSelected
                                  ? 'border-[hsl(var(--gold))] bg-card ring-2 ring-[hsl(var(--gold))]/30 shadow-sm'
                                  : 'border-border/60 bg-card/50 hover:border-border hover:bg-card'}`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-sm font-medium capitalize flex items-center gap-2">
                                    {c === 'annual' ? 'Annual' : 'Monthly'}
                                    {c === 'annual' && (
                                      <span className="text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--gold))] bg-[hsl(var(--gold))]/12 border border-[hsl(var(--gold))]/30 rounded-full px-2 py-0.5">
                                        2 mo free
                                      </span>
                                    )}
                                  </div>
                                  {isSelected && (
                                    <div className="h-5 w-5 rounded-full bg-[hsl(var(--gold))] grid place-items-center shrink-0">
                                      <Check className="h-3 w-3 text-[hsl(var(--gold-foreground))]" strokeWidth={3} />
                                    </div>
                                  )}
                                </div>
                                <div className="mt-2 flex items-baseline gap-1.5">
                                  <div className="font-display text-2xl font-semibold tabular-nums tracking-tight">
                                    {formatMoney(p.amount, p.currency, { decimals })}
                                  </div>
                                  <div className="text-xs text-muted-foreground">/ {per}</div>
                                </div>
                                {me ? (
                                  <div className="text-[11px] text-muted-foreground mt-0.5">
                                    ≈ {formatMoney(me, p.currency, { decimals })}/month · billed annually
                                  </div>
                                ) : c === 'monthly' && annualSavings > 0 ? (
                                  <div className="text-[11px] text-muted-foreground mt-0.5">
                                    Save {formatMoney(annualSavings, p.currency, { decimals })} by going annual
                                  </div>
                                ) : (
                                  <div className="text-[11px] text-muted-foreground mt-0.5">&nbsp;</div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="mt-4 flex items-center gap-3 flex-wrap">
                    <Button
                      onClick={() => {
                        setPendingUpgrade({ plan: eff, cycle: selectedCycle });
                        setTimeout(() => {
                          try {
                            document.getElementById('billing-confirm-upgrade')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          } catch (e) { /* no-op */ }
                        }, 50);
                      }}
                      disabled={busy}
                      // Phase 37.1, explicit, high-contrast hover state.
                      // `hover:opacity-90` used to leave the text colour at
                      // whatever the inherited gold-foreground was, which
                      // could read as black-on-green when the button bg
                      // resolved to a darker gold and the underlying card
                      // surface bled through the opacity. We now lock both
                      // bg AND text colour explicitly across all states.
                      className="rounded-xl bg-[hsl(var(--gold))] text-[hsl(var(--gold-foreground))] hover:bg-[hsl(var(--gold))] hover:brightness-95 focus-visible:bg-[hsl(var(--gold))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--gold))]/40 shadow-sm"
                      data-testid="billing-add-payment-method"
                    >
                      <CreditCard className="h-4 w-4 mr-1.5" /> Continue with {selectedCycle}
                    </Button>
                    <button
                      type="button"
                      onClick={handleSkipTrial}
                      disabled={busy}
                      data-testid="billing-skip-trial"
                      className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline disabled:opacity-50"
                    >
                      Or switch to Free forever
                    </button>
                  </div>
                </div>
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Payment method card (only meaningful for paid users) */}
      {(isPaid || isTrialing) && (
        <Card className="rounded-2xl border-border/70" data-testid="billing-payment-method-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Payment method</div>
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <span className="text-foreground/85">
                    {isTrialing && !sub?.provider_subscription_id ? 'No card on file yet' : 'Managed by your provider'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  We don’t store your full card number. Card management happens in the secure customer portal.
                </p>
              </div>
              <Button onClick={openPortal} disabled={busy} variant="ghost" className="rounded-xl" data-testid="billing-update-card">
                Update card <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices card */}
      <Card className="rounded-2xl border-border/70" data-testid="billing-invoices-card">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Receipt className="h-4 w-4 text-muted-foreground" />
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Recent invoices</div>
          </div>
          {invoices.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center" data-testid="billing-invoices-empty">
              No invoices yet. They’ll appear here once your first payment is processed.
            </div>
          ) : (
            <ul className="divide-y divide-border/60" data-testid="billing-invoices-list">
              {invoices.map((inv) => (
                <li key={inv.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{inv.number || `Invoice ${inv.id.slice(0, 8)}`}</div>
                    <div className="text-xs text-muted-foreground">
                      {fmtDate(inv.paid_at || inv.created_at)} · {(inv.status || 'paid').toUpperCase()}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-sm tabular-nums">
                      {formatMoney((inv.amount_cents || 0) / 100, inv.currency || 'USD', { decimals: inv.currency === 'USD' ? 2 : 0 })}
                    </div>
                    {inv.hosted_invoice_url && (
                      <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-primary text-xs hover:underline inline-flex items-center gap-1">
                        View <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Footer trust strip */}
      <Card className="rounded-2xl border-border/70 bg-accent/40">
        <CardContent className="p-5 sm:p-6 flex items-start gap-3">
          <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <div className="text-xs text-muted-foreground leading-relaxed">
            <div className="text-sm font-medium text-foreground mb-1">Secure billing</div>
            All payments are processed by <span className="font-medium text-foreground">Stripe</span> (USD) and <span className="font-medium text-foreground">Razorpay</span> (INR). 2120 never stores your full card number. For our refund stance, see the <Link to="/refunds" className="text-primary hover:underline">Refund Policy</Link>.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
